import { resolveTemporalDateColumns } from '@streamlain/database-identity';
import { createCanonicalCalendarViewData } from '@streamlain/database-identity/database-view-defaults';

import type { GroupBy, GroupProperty } from '../../core/common/types.js';
import type { FilterGroup } from '../../core/filter/types.js';
import type { Sort } from '../../core/sort/types.js';
import { type BasicViewDataType, viewType } from '../../core/view/data-view.js';
import { defaultTemporalProfileColumnName } from '../temporal-profile.js';
import { CalendarSingleView } from './calendar-view-manager.js';
import {
  type CalendarDisplaySettings,
  DEFAULT_CALENDAR_DISPLAY_SETTINGS,
} from './pc/calendar-display-policy.js';

export const calendarViewType = viewType('calendar');

export type CalendarMode = 'day' | 'week' | 'month';

export type CalendarViewColumn = {
  id: string;
  hide?: boolean;
  width?: number;
};

export type CalendarIcsSubscription = {
  enabled?: boolean;
  id: string;
  name?: string;
  url: string;
};

type DataType = {
  columns: CalendarViewColumn[];
  filter: FilterGroup;
  groupBy?: GroupBy;
  groupProperties?: GroupProperty[];
  sort?: Sort;
  header?: {
    titleColumn?: string;
    iconColumn?: string;
  };
  startDateCol?: string;
  endDateCol?: string;
  allDayCol?: string;
  timezoneCol?: string;
  recurrenceCol?: string;
  recurrenceExdateCol?: string;
  recurrenceIdCol?: string;
  calendarIdCol?: string;
  locationPhysicalCol?: string;
  locationVirtualCol?: string;
  descriptionCol?: string;
  attendeesCol?: string;
  reminderCol?: string;
  visibilityCol?: string;
  statusCol?: string;
  assigneeCol?: string;
  progressCol?: string;
  dependenciesCol?: string;
  icsSequenceCol?: string;
  icsUidCol?: string;
  invitationMethodCol?: string;
  invitationStateCol?: string;
  sourceMailAccountIdCol?: string;
  sourceMailAttachmentBlobKeyCol?: string;
  sourceMailMessageIdCol?: string;
  sourceTypeCol?: string;
  calendarCursorTime?: number;
  calendarMode?: CalendarMode;
  displaySettings?: CalendarDisplaySettings;
  icsSubscriptions?: CalendarIcsSubscription[];
  visibleCalendarIds?: string[];
};

export type CalendarViewData = BasicViewDataType<
  typeof calendarViewType.type,
  DataType
>;

export const calendarViewModel = calendarViewType.createModel<CalendarViewData>(
  {
    defaultName: 'Calendar View',
    dataViewManager: CalendarSingleView,
    defaultData: viewManager => {
      const columns =
        viewManager.dataSource.getBoundedPropertyIdsWindow().propertyIds;
      const resolved = resolveTemporalDateColumns(
        viewManager.dataSource,
        viewManager.dataSource.viewDataList$.value
      );
      const resolvedStartDateCol =
        resolved.startDateCol ??
        viewManager.dataSource.propertyAdd('end', {
          type: 'date',
          name: defaultTemporalProfileColumnName('start'),
        });
      const resolvedEndDateCol =
        resolved.endDateCol ??
        viewManager.dataSource.propertyAdd('end', {
          type: 'date',
          name: defaultTemporalProfileColumnName('end'),
        });
      return {
        ...createCanonicalCalendarViewData({
          properties: columns.map(id => ({
            id,
            type: viewManager.dataSource.propertyTypeGet(id) ?? '',
            name: viewManager.dataSource.propertyNameGet(id),
          })),
          startDateCol: resolvedStartDateCol,
          endDateCol: resolvedEndDateCol,
        }),
        displaySettings: DEFAULT_CALENDAR_DISPLAY_SETTINGS,
      };
    },
  }
);
